import { createSlice } from "@reduxjs/toolkit";
import dayjs from "dayjs";

const initialState = {
  tab: 0,
  calculator: {
    commercial: {
      selectedServices: [],
      configuration: {
        benefits: "",
        workCompensation: "",
        tax: "",
        hasMileage: false,
        mileageRate: "",
        mileageDistance: 0,
        frequency: "",
        interval: 1,
        duration: "",
        grossProfit: "",
        averagePeople: "",
        staffRatePerHour: [],
        dates: [],
        supplies: [],
        contractOverride: [
          {
            startDate: dayjs().format(),
            endDate: dayjs().format(),
            hours: [
              {
                startHour: dayjs().set("minute", 0).set("second", 0).format(),
                endHour: dayjs().add(1, "hour").set("minute", 0).set("second", 0).format(),
              },
            ],
          },
        ],
        isFrequently: true,
        mileage: {
          hasMileage: false,
          location: "",
          address: "",
          latitude: "",
          longitude: "",
          mileageDistance: "",
          mileageReimbursement: "",
        },
      },
      serviceQuotes: [
        {
          serviceId: null,
          service: "",
          squareFt: 0,
          frequency: {
            type: "",
            interval: 0,
            duration: 0,
          },
          labor: {
            grossProfit: 0,
            cost: 0,
            totalCogs: 0,
            totalGp: 0,
            hourlyRate: 0,
            cogsPercentaje: 0,
            gpPercent: 0,
          },
          baseRate: {
            costPerSf: 0,
            serviceCost: 0,
            monthlyRate: 0,
          },
          supervisor: {
            costPerSf: 0,
            serviceCost: 0,
            monthlyRate: 0,
          },
          consumables: null,
          supplies: {
            items: [],
            costPerService: 0,
            monthlyCost: 0,
          },
          totalsCalc: {
            costPerSf: 0,
            serviceCost: 0,
            monthlyRate: 0,
          },
        },
      ],
    },
    residential: {
      workTypeId: null,
      grossProfit: null,
      operationExpenses: {
        benefits: null,
        workCompensation: null,
        tax: null,
      },
      staffRatePerHour: [],
      // frequencyOverride: {
      //     type: "",
      //     interval: null,
      //     duration: null,
      // },
      workScopeService: {
        workServiceId: null,
        squareFt: null,
        quoteWorkScopeServices: {
          supplies: [],
          consumables: [],
          mileageReimbursement: {
            rate: null,
            distance: null,
          },
        },
        staffHours: [],
      },
      averagePeople: null,
      // contractOverride: [],
      rooms: {
        bedroom: "",
        otherRoom: "",
        masterBathroom: "",
        regularBathroom: "",
        halfBathroom: "",
      },
      areas: {
        kitchen: false,
        livingRoom: false,
        laundryRoom: false,
        stairs: false,
      },
      additionalRequest: {
        fridge: false,
        dishwasher: false,
        tidyUp: false,
        windows: false,
        oven: false,
        handDish: false,
        garage: false,
      },
      pets: false,
      mileage: {
        hasMileage: false,
        location: "",
        address: "",
        latitude: "",
        longitude: "",
        mileageDistance: "",
        mileageReimbursement: "",
      },
    },
  },
};

const calculatorSlice = createSlice({
  name: "calculatorSlice",
  initialState,
  reducers: {
    setCommercialServices: (state, action) => {
      state.calculator.commercial.serviceQuotes = action.payload;
    },
    updateCommercialConfiguration: (state, action) => {
      state.calculator.commercial.configuration = {
        ...state.calculator.commercial.configuration,
        ...action.payload,
      };
    },
    updateCommercialServiceById: (state, action) => {
      const { id, service } = action.payload;

      state.calculator.commercial.selectedServices = state.calculator.commercial.selectedServices.map((svc) => {
        if (svc.workServiceId === id) {
          return {
            ...svc,
            ...service,
          };
        }
        return svc;
      });
    },
    updateCommercialCalculator: (state, action) => {
      const payload = action.payload;

      state.calculator.commercial = {
        ...state.calculator.commercial,
        ...payload,
        operationExpenses: {
          ...state.calculator.commercial.operationExpenses,
          ...payload.operationExpenses,
        },
        workScopeServices: payload.workScopeServices
          ? payload.workScopeServices.map((incomingScope, index) => {
              const existingScope = state.calculator.commercial.workScopeServices?.[index] || {};
              return {
                ...existingScope,
                ...incomingScope,
                quoteWorkScopeServices: {
                  ...existingScope.quoteWorkScopeServices,
                  ...incomingScope.quoteWorkScopeServices,
                  mileageReimbursement: {
                    ...existingScope.quoteWorkScopeServices?.mileageReimbursement,
                    ...incomingScope.quoteWorkScopeServices?.mileageReimbursement,
                  },
                },
                staffHours: incomingScope.staffHours || existingScope.staffHours || [],
              };
            })
          : state.calculator.commercial.workScopeServices,
      };
    },
    updateResidentialCalculator: (state, action) => {
      state.calculator.residential = {
        ...state.calculator.residential,
        ...action.payload,
        workScopeService: {
          ...state.calculator.residential.workScopeService,
          ...action.payload.workScopeService,
          quoteWorkScopeServices: {
            ...state.calculator.residential.workScopeService?.quoteWorkScopeServices,
            ...action.payload.workScopeService?.quoteWorkScopeServices,
          },
        },
      };
    },
    updateCalculatorTab: (state, action) => {
      state.tab = action.payload;
    },
    updateSelectedServices: (state, action) => {
      const incomingServices = action.payload;
      const currentServices = state.calculator.commercial.selectedServices;

      const existingMap = Object.fromEntries(currentServices.map((svc) => [svc.workServiceId, svc]));

      const mergedServices = incomingServices.map((svc) => {
        const id = svc.workServiceId || svc.id;
        const existing = existingMap[id] || {};

        const merged = {
          ...existing,
          ...svc,
          workServiceId: id,
          staffHours:
            Array.isArray(svc.staffHours) && svc.staffHours.length > 0
              ? svc.staffHours
              : existing.staffHours || [{ type: "", quantity: "", hoursPerStaff: "" }],
          squareFt: svc.squareFt ?? existing.squareFt ?? 0,
          supplies: svc.supplies ?? existing.supplies ?? [],
        };

        return merged;
      });

      state.calculator.commercial.selectedServices = mergedServices;
    },
    updateServiceById: (state, action) => {
      const { id, service } = action.payload;

      state.calculator.commercial.selectedServices = state.calculator.commercial.selectedServices.map((svc) => {
        if (svc.workServiceId === id) {
          return {
            ...svc,
            ...service,
          };
        }
        return svc;
      });
    },
  updateCommercialMileage: (state, action) => {
    const updates = action.payload;
    
    state.calculator.commercial.configuration.mileage = {
      ...state.calculator.commercial.configuration.mileage,
      ...updates,
    };
  }
  },
});

export const {
  updateCommercialCalculator,
  updateResidentialCalculator,
  updateCalculatorTab,
  updateSelectedServices,
  updateServiceById,
  updateCommercialConfiguration,
  updateCommercialServiceById,
  updateCommercialMileage,
} = calculatorSlice.actions;
export default calculatorSlice.reducer;
